<template>
  <div>
    <h1 class="title">{{ $t('customer.linkTranscript.title') }}</h1>
    <v-container fluid class="pb-4">
      <v-row justify="center">
        <v-col cols="12" sm="6">
          <v-card
            min-height="200px"
            width="500"
            class="mx-auto mt-5 pa-4"
            elevation="10"
          >

            <CardTitleWithTooltip>
              <template v-slot:title>
                  {{ $t('customer.linkTranscript.configuration') }}
              </template>
              <template v-slot:tooltip>
                  {{ $t('customer.linkTranscript.configurationTooltip') }}
              </template>
            </CardTitleWithTooltip>

            <v-card-text class="pa-0">
              <v-form ref="twilioMessagingDataKeysForm" v-model="twilioMessagingDataKeysFormValidity" @submit.prevent="saveTwilioMessagingDataKeys">
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete 
                        :label="$t('customer.linkTranscript.basisHeading')+'*'" 
                        :items="linkTranscriptBasis" 
                        v-model="twilioMessagingDataKeys.linkTranscriptBasis"
                      >
                        <template v-slot:item="data">
                          <v-tooltip top>
                            <template slot="activator" slot-scope="{ on }">
                              <v-list-tile-content>
                                <v-list-tile-title v-html="data.item" v-on="on"></v-list-tile-title>
                              </v-list-tile-content>
                            </template>
                            <span v-html="$t('customer.linkTranscript.'+ data.item.toLowerCase())"></span>
                        </v-tooltip>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field 
                        :label="$t('customer.linkTranscript.accountSID')+'*'" 
                        :type="showaccountSid? 'text' : 'password'"
                        :append-icon="showaccountSid ? 'mdi-eye' : 'mdi-eye-off'"
                        v-model="twilioMessagingDataKeys.accountSid"
                        @click:append="showaccountSid = !showaccountSid"
                        tabindex="2"
                        required
                        dense
                        :rules="twilioLinkTranscriptConfigRules.accountSid" 
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field 
                        :label="$t('customer.linkTranscript.authToken')+'*'" 
                        :type="showAuthToken ? 'text' : 'password'"
                        :append-icon="showAuthToken ? 'mdi-eye' : 'mdi-eye-off'"
                        v-model="twilioMessagingDataKeys.authToken"
                        @click:append="showAuthToken = !showAuthToken"
                        tabindex="3"
                        required
                        dense
                        :rules="twilioLinkTranscriptConfigRules.authToken"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field 
                        :label="$t('customer.linkTranscript.serviceInstanceID')+'*'" 
                        :type="showServiceSid ? 'text' : 'password'"
                        :append-icon="showServiceSid ? 'mdi-eye' : 'mdi-eye-off'"
                        v-model="twilioMessagingDataKeys.serviceSid"
                        @click:append="showServiceSid = !showServiceSid"
                        tabindex="4"
                        required
                        dense
                        :rules="twilioLinkTranscriptConfigRules.serviceSid"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card-text>

            <v-card-actions class="pa-0 justify-center">
              <v-btn
                @click="dialogTwilioMessagingDataKeysConfirm = true"
                :disabled="!twilioMessagingDataKeysFormValidity"
                v-bind="size"
                class="px-2" 
                color="primary"
                dark               
              >
                {{ $t('save') }}
              </v-btn>
              <ConfirmDialog
                v-if="dialogTwilioMessagingDataKeysConfirm"
                :confirmDialogShow="dialogTwilioMessagingDataKeysConfirm"
                @confirm-dialog-close="dialogTwilioMessagingDataKeysConfirm = false"
                @confirm-dialog-save="saveTwilioMessagingDataKeys"
              />
              </v-card-actions>

          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import { mapMutations, mapGetters } from 'vuex'
  import customerOps from '../api/users'
  import { customerMixin } from '../mixins/customerMixins'
  import i18n from '../i18n'
  import resizeMixin from '../mixins/resizeMixins'

  export default {
    mixins: [customerMixin, resizeMixin],
    components: {
      CardTitleWithTooltip: require('../components/CardTitleWithTooltip').default,
      ConfirmDialog: () => import('@/components/ConfirmDialog'),
    },
    computed: {
      ...mapGetters({
        getCustomerProfile: 'users/getCustomerProfile',
        getCustomerTwilioMessagingDataKeys: 'users/getCustomerMessagingDataKeys',
      }),
    },
    created() {
      // Customer Profile data
      this.customerProfile = { ...this.getCustomerProfile }

      // Customer Twilio Messaging Data Keys
      this.twilioMessagingDataKeys = { ...this.getCustomerTwilioMessagingDataKeys }
      this.twilioMessagingDataKeys.linkTranscriptBasis = this.twilioMessagingDataKeys.linkTranscriptBasis ? 
                                                        this.twilioMessagingDataKeys.linkTranscriptBasis: this.linkTranscriptBasis[0];      
      this.twilioMessagingDataKeys.linkTranscriptBasis = this.toCapitalCase(this.twilioMessagingDataKeys.linkTranscriptBasis)

      // Customer Twilio Flex Link Transcript Configuration Rules
      this.twilioLinkTranscriptConfigRules = i18n.t(this.linkTranscriptConfigRules.twilioFlex)
    },
    data () {
      return {
        customerProfile: {},
        dialogTwilioMessagingDataKeys: false,
        dialogTwilioMessagingDataKeysConfirm: false,
        linkTranscriptBasis: ['Immediate', 'Scheduled'],
        showaccountSid: false,
        showAuthToken: false,
        showServiceSid: false,
        snackbar: false,
        twilioMessagingDataKeys: {},
        twilioMessagingDataKeysFormValidity: false,
        twilioLinkTranscriptConfigRules: {},
      }
    },
    methods: {
      clearTwilioMessagingDataKeys (){        
        this.twilioMessagingDataKeys = {};
      },
      ...mapMutations({
        setSnack: 'setSnack',
        SET_USER_MESSAGINGDATAKEYS: 'users/SET_USER_MESSAGINGDATAKEYS'
      }),
      saveTwilioMessagingDataKeys(){

        this.dialogTwilioMessagingDataKeysConfirm = false
        this.dialogTwilioMessagingDataKeys = false

        const updateTwilioMessagingKeysData = {
          strict: true,
          value: {
            op: "TFDATAUPDATE",
            id: this.customerProfile.id,
            accountId: this.customerProfile.accountId,
            data: {
              linkTranscriptBasis: this.twilioMessagingDataKeys.linkTranscriptBasis.toLowerCase(), 
              accountSid: this.twilioMessagingDataKeys.accountSid ? this.twilioMessagingDataKeys.accountSid : '',
              authToken: this.twilioMessagingDataKeys.authToken ? this.twilioMessagingDataKeys.authToken : '',
              serviceSid: this.twilioMessagingDataKeys.serviceSid ? this.twilioMessagingDataKeys.serviceSid : ''
            }
          }
        }

        customerOps.customerUpdateMessagingKeys(updateTwilioMessagingKeysData).then(response => {

          // Snackbar success
          this.showSnack(i18n.t('customer.linkTranscript.updateSuccess'), false);
          this.scrollToTop();

          this.twilioMessagingDataKeys = response.value.data.twilioflexDataKeys;
          this.twilioMessagingDataKeys.linkTranscriptBasis = this.toCapitalCase(this.twilioMessagingDataKeys.linkTranscriptBasis)
 
          // Updating data in store and sessionStorage
          let storageData  = JSON.parse(sessionStorage.getItem('user'));
          storageData.value.data.twilioflexDataKeys =  response.value.data.twilioflexDataKeys;
          sessionStorage.setItem("user", JSON.stringify(storageData));

          this.SET_USER_MESSAGINGDATAKEYS(response.value.data.twilioflexDataKeys);

        })
        .catch(err => {
          console.error(`[CustomerLinkTranscript] - saveTwilioMessagingDataKeys error: `, err)

          // Snackbar error
          this.showSnack(i18n.t('customer.linkTranscript.updateError'), true);
          this.scrollToTop();
        })
      },
      showSnack: function (snack, isError) {
        const setSnackObj = {
          message: snack,
          isError: !!isError
        }
        this.setSnack(setSnackObj)
      },
    }
  }
</script>

<style lang="scss" scoped>
</style>